import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'lib-file-selector',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule],
    template: `
    <div class="file-selector-container" 
         [class.drag-over]="isDragOver()"
         (dragover)="onDragOver($event)" 
         (dragleave)="onDragLeave($event)" 
         (drop)="onDrop($event)">
        
        <!-- Drag Overlay -->
        <div *ngIf="isDragOver()" class="drag-overlay animate-in fade-in zoom-in duration-300">
            <div class="overlay-content">
                <div class="icon-wrapper">
                    <mat-icon class="overlay-icon">{{ dragIcon }}</mat-icon>
                </div>
                <h2 class="overlay-text">{{ dragTitle }}</h2>
                <p class="overlay-subtext">{{ dragSubtext }}</p>
            </div>
        </div>

        <div class="file-selector-content">
            <div class="text-center">
                <div class="actions">
                    <button mat-flat-button color="primary" class="!px-8 !py-6 !text-lg" (click)="fileInput.click()">
                        <mat-icon class="!w-6 !h-6 !text-2xl mr-2">{{ buttonIcon }}</mat-icon>
                        {{ buttonLabel }}
                    </button>
                    <p class="mt-6 text-base opacity-60">or drop files here</p>
                </div>
            </div>
        </div>

        <input type="file" #fileInput hidden [multiple]="multiple" [accept]="accept" (change)="onFileSelected($event)">
    </div>
  `,
    styles: [`
    .file-selector-container {
        position: relative;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px dashed rgba(var(--mat-sys-outline), 0.1);
        border-radius: 24px;
        transition: all 0.3s ease;
        background: rgba(var(--mat-sys-surface-container-low), 0.5);
    }

    .file-selector-container.drag-over {
        border-color: var(--mat-sys-primary);
        background: color-mix(in srgb, var(--mat-sys-primary), transparent 95%);
    }

    .drag-overlay {
        position: absolute;
        inset: 0;
        z-index: 50;
        background: color-mix(in srgb, var(--mat-sys-primary), transparent 10%);
        backdrop-filter: blur(8px);
        border-radius: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--mat-sys-on-primary);
        pointer-events: none;
    }

    .overlay-content {
        text-align: center;
    }

    .icon-wrapper {
        width: 80px;
        height: 80px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;
    }

    .overlay-icon {
        font-size: 40px;
        width: 40px;
        height: 40px;
    }

    .overlay-text {
        font-size: 2rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
    }

    .overlay-subtext {
        font-size: 1.1rem;
        opacity: 0.9;
    }

    .file-selector-content {
        padding: 1.5rem;
        max-width: 600px;
        width: 100%;
    }


    @media (max-width: 640px) {
        .title {
            font-size: 1.8rem;
        }
        .file-selector-content {
            padding: 1.5rem;
        }
    }
  `]
})
export class FileSelectorComponent {
    @Input() buttonLabel = 'Choose Files';
    @Input() buttonIcon = 'add_circle_outline';
    @Input() accept = '*/*';
    @Input() multiple = true;

    @Input() dragTitle = 'Drop to upload';
    @Input() dragSubtext = 'Release to start processing ✨';
    @Input() dragIcon = 'drive_file_move';

    @Output() filesSelected = new EventEmitter<File[]>();

    isDragOver = signal<boolean>(false);

    onDragOver(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(true);
    }

    onDragLeave(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(false);
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver.set(false);

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.filesSelected.emit(Array.from(files));
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.filesSelected.emit(Array.from(input.files));
        }
    }
}
